/**
 * MapViewQ1Unified — cuadrante 1 del MapView en modo split.
 *
 * Reemplaza al LivePreviewPanel + CountsStrip viejos. Unifica video del
 * canal activo con un strip compacto de salud (decisión Paulo 2026-06-11:
 * "elimino autito y personita, sumo salud al video").
 *
 * Recibe cameraId como prop controlada — el padre MapView la cambia
 * cuando el operador clickea una cámara en Q4 (plano). Si cameraId es
 * null, default al primer canal del sitio.
 *
 * Strip de salud arriba del video con 5 chips compactos:
 *   - estado online/offline
 *   - FPS de captura
 *   - latencia HLS (ms)
 *   - analíticas activas (chips: P V LPR L LC)
 *   - CPU% / MEM% del nodo edge
 */

import { useState, useEffect, useMemo } from 'react'
import HlsPlayer, { buildHlsUrl } from './HlsPlayer'


export default function MapViewQ1Unified({
  site,
  token,
  cameraId,                    // null = primer canal del primer NVR del sitio
  streamQuality = 'main',      // 'rtc' | 'main' | 'sub'
  onChangeQuality,
}) {
  const [cameras, setCameras] = useState([])
  const [health, setHealth]   = useState(null)
  const [err, setErr]         = useState(null)

  // Cargar cámaras del sitio para resolver cameraId default y obtener NVR/channel
  useEffect(() => {
    if (!site?.id || !token) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch(`/api/cameras/site/${site.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        if (!cancelled) setCameras(Array.isArray(data) ? data : [])
      } catch (e) {
        if (!cancelled) setErr(e.message)
      }
    })()
    return () => { cancelled = true }
  }, [site?.id, token])

  // Cargar salud agregada del edge node del sitio (resumen del Pilar 2)
  useEffect(() => {
    if (!site?.id || !token) return
    const fetchHealth = async () => {
      try {
        const r = await fetch(`/api/edge/health/${site.id}/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) return
        const data = await r.json()
        setHealth(data)
      } catch {}
    }
    fetchHealth()
    const t = setInterval(fetchHealth, 15000)   // refresh 15s
    return () => clearInterval(t)
  }, [site?.id, token])

  // Resolver cámara activa: controlada por prop o default al primer canal
  const activeCamera = useMemo(() => {
    if (cameraId) return cameras.find(c => c.id === cameraId)
    return cameras.find(c => c.enabled) || cameras[0] || null
  }, [cameras, cameraId])

  // Salud específica de la cámara activa (si la API lo expone)
  const cameraHealth = useMemo(() => {
    if (!health?.cameras || !activeCamera) return null
    return health.cameras.find(c => c.id === activeCamera.id) || null
  }, [health, activeCamera])

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      width: '100%', height: '100%',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Strip de salud — arriba del video, compacto */}
      <HealthStrip
        camera={activeCamera}
        cameraHealth={cameraHealth}
        nodeHealth={health}
        streamQuality={streamQuality}
        onChangeQuality={onChangeQuality}
      />

      {/* Video del canal activo */}
      <div style={{ flex: 1, position: 'relative', background: '#000' }}>
        {activeCamera && activeCamera.nvr_id && (
          <HlsPlayer
            url={buildHlsUrl(site.id, activeCamera.nvr_id, activeCamera.channel)}
            channelName={activeCamera.name}
            posterUrl={`/api/nvr/${activeCamera.nvr_id}/snapshot/${activeCamera.channel}`}
          />
        )}
        {!activeCamera && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', fontSize: 13,
          }}>
            Sin cámaras configuradas en este sitio
          </div>
        )}
        {err && (
          <div style={{
            position: 'absolute', top: 8, left: 8,
            padding: '4px 10px',
            background: 'rgba(217, 48, 37, 0.95)',
            color: 'white', borderRadius: 4, fontSize: 11,
          }}>{err}</div>
        )}
      </div>
    </div>
  )
}


// ─── Strip de salud — barra fina sobre el video ──────────────────────────

function HealthStrip({ camera, cameraHealth, nodeHealth, streamQuality, onChangeQuality }) {
  const chipStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', fontSize: 10, fontWeight: 600,
    borderRadius: 10, letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  }

  const online    = cameraHealth?.status === 'online' || cameraHealth?.online
  const fps       = cameraHealth?.fps_captured ?? camera?.fps_analytics
  const latencyMs = cameraHealth?.latency_ms
  const cpuPct    = nodeHealth?.cpu_percent
  const memPct    = nodeHealth?.mem_percent

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      background: 'var(--bg-elev-1)',
      borderBottom: '1px solid var(--border)',
      fontSize: 11,
      flexWrap: 'wrap',
    }}>
      {/* Nombre del canal activo */}
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', flexShrink: 0 }}>
        {camera ? `CH${String(camera.channel).padStart(2,'0')} · ${camera.name}` : '—'}
      </div>

      {/* Estado online */}
      <span style={{
        ...chipStyle,
        background: online == null ? 'transparent'
          : online ? 'rgba(34, 197, 94, 0.15)' : 'rgba(217, 48, 37, 0.15)',
        color: online == null ? 'var(--text-muted)'
          : online ? '#16a34a' : '#d93025',
        border: '1px solid currentColor',
      }}>
        {online == null ? '— Estado' : online ? '● Online' : '● Offline'}
      </span>

      {/* FPS */}
      {fps != null && (
        <span style={{ ...chipStyle, background: 'rgba(0,145,255,0.1)', color: '#0091ff' }}>
          {fps} FPS
        </span>
      )}

      {/* Latencia */}
      {latencyMs != null && (
        <span style={{
          ...chipStyle,
          background: latencyMs < 500 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.15)',
          color: latencyMs < 500 ? '#16a34a' : '#d97706',
        }}>
          {latencyMs}ms
        </span>
      )}

      {/* Analíticas activas */}
      {camera && (
        <div style={{ display: 'flex', gap: 3 }}>
          {(camera.analytics || []).map(a => (
            <span key={a} style={{
              ...chipStyle,
              background: 'rgba(99, 102, 241, 0.15)',
              color: '#6366f1',
              padding: '2px 6px',
              fontSize: 9,
            }} title={a}>
              {analyticsShort(a)}
            </span>
          ))}
          {camera.loitering_seconds && (
            <span style={{
              ...chipStyle, background: 'rgba(99, 102, 241, 0.15)',
              color: '#6366f1', padding: '2px 6px', fontSize: 9,
            }}>L</span>
          )}
          {camera.protected_line && (
            <span style={{
              ...chipStyle, background: 'rgba(99, 102, 241, 0.15)',
              color: '#6366f1', padding: '2px 6px', fontSize: 9,
            }}>LC</span>
          )}
        </div>
      )}

      {/* CPU / MEM del nodo edge */}
      {(cpuPct != null || memPct != null) && (
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {cpuPct != null && (
            <span style={{
              ...chipStyle,
              background: cpuPct > 80 ? 'rgba(217, 48, 37, 0.15)' : 'rgba(107, 114, 128, 0.15)',
              color: cpuPct > 80 ? '#d93025' : 'var(--text-secondary)',
            }}>CPU {cpuPct.toFixed(0)}%</span>
          )}
          {memPct != null && (
            <span style={{
              ...chipStyle,
              background: memPct > 80 ? 'rgba(217, 48, 37, 0.15)' : 'rgba(107, 114, 128, 0.15)',
              color: memPct > 80 ? '#d93025' : 'var(--text-secondary)',
            }}>MEM {memPct.toFixed(0)}%</span>
          )}
        </div>
      )}

      {/* Toggle RTC / HD / SD */}
      <div style={{
        display: 'flex', borderRadius: 4,
        border: '1px solid var(--border)', overflow: 'hidden',
      }}>
        {['rtc', 'main', 'sub'].map(q => (
          <button
            key={q}
            onClick={() => onChangeQuality && onChangeQuality(q)}
            style={{
              padding: '2px 6px', fontSize: 9, fontWeight: 700,
              border: 'none', cursor: 'pointer',
              background: streamQuality === q ? '#0091ff' : 'var(--bg-surface)',
              color: streamQuality === q ? 'white' : 'var(--text-muted)',
              letterSpacing: '0.04em',
            }}
          >{q === 'rtc' ? 'RTC' : q === 'main' ? 'HD' : 'SD'}</button>
        ))}
      </div>
    </div>
  )
}


function analyticsShort(a) {
  switch (a) {
    case 'person': return 'P'
    case 'vehicle': return 'V'
    case 'lpr':    return 'LPR'
    default: return a.slice(0, 3).toUpperCase()
  }
}
